import colors from './colors';

/**
 * Maps semantic color names (from Blockly metadata) to theme color pairs
 * for display modifiers (RowHighlight and PillBox).
 *
 * Color names come from the `color` property on records (e.g. order:fulfillmentStatus).
 * The mapping follows the Order Confirmation design doc color table.
 */

const colorMap = {
    'Yellow Green': {
        light: { background: colors.yellowGreen50, foreground: colors.yellowGreen800 },
        dark: { background: colors.yellowGreen800, foreground: colors.yellowGreen50 }
    },
    Green: {
        light: { background: colors.green50, foreground: colors.green800 },
        dark: { background: colors.green800, foreground: colors.green50 }
    },
    Teal: {
        light: { background: colors.teal50, foreground: colors.teal800 },
        dark: { background: colors.teal800, foreground: colors.teal50 }
    },
    Blue: {
        light: { background: colors.primary50, foreground: colors.primary800 },
        dark: { background: colors.primary800, foreground: colors.primary50 }
    },
    'Cool Gray': {
        light: { background: colors.coolGray100, foreground: colors.coolGray800 },
        dark: { background: colors.coolGray700, foreground: colors.coolGray100 }
    },
    Periwinkle: {
        light: { background: colors.periwinkle50, foreground: colors.periwinkle800 },
        dark: { background: colors.periwinkle800, foreground: colors.periwinkle50 }
    },
    Purple: {
        light: { background: colors.purple50, foreground: colors.purple800 },
        dark: { background: colors.purple800, foreground: colors.purple50 }
    },
    Rose: {
        light: { background: colors.rose50, foreground: colors.rose800 },
        dark: { background: colors.rose800, foreground: colors.rose50 }
    },
    Red: {
        light: { background: colors.red50, foreground: colors.red700 },
        dark: { background: colors.red800, foreground: colors.red50 }
    },
    Orange: {
        light: { background: colors.orange50, foreground: colors.orange700 },
        dark: { background: colors.orange800, foreground: colors.orange50 }
    },
    Yellow: {
        light: { background: colors.yellow50, foreground: colors.yellow800 },
        dark: { background: colors.yellow800, foreground: colors.yellow50 }
    }
};

// Build a case-insensitive lookup from the canonical map
const normalizedColorMap = {};
for (const [name, value] of Object.entries(colorMap)) {
    normalizedColorMap[name.toLowerCase()] = value;
}

/**
 * Resolve a color name to background/foreground pair for the current mode.
 * @param {string} colorName - Color name from the record (e.g. "Green", "Red")
 * @param {boolean} darkMode - Whether dark mode is active
 * @returns {{ background: string, foreground: string } | null}
 */
export function resolveDisplayModifierColor(colorName, darkMode) {
    if (!colorName) return null;
    const entry = normalizedColorMap[colorName.toLowerCase()];
    if (!entry) return null;
    return darkMode ? entry.dark : entry.light;
}

// Border color map (200/600 variants) — hoisted to module scope to avoid per-call allocation
const normalizedBorderMap = {
    'yellow green': { light: colors.yellowGreen200, dark: colors.yellowGreen600 },
    green: { light: colors.green200, dark: colors.green600 },
    teal: { light: colors.teal200, dark: colors.teal600 },
    blue: { light: colors.primary200, dark: colors.primary600 },
    'cool gray': { light: colors.coolGray200, dark: colors.coolGray600 },
    periwinkle: { light: colors.periwinkle200, dark: colors.periwinkle600 },
    purple: { light: colors.purple200, dark: colors.purple600 },
    rose: { light: colors.rose200, dark: colors.rose600 },
    red: { light: colors.red200, dark: colors.red600 },
    orange: { light: colors.orange200, dark: colors.orange600 },
    yellow: { light: colors.yellow200, dark: colors.yellow600 }
};

/**
 * Resolve pill box border color (200 variant for light, 600 variant for dark).
 * Used for card borders when PillBox is active.
 * @param {string} colorName
 * @param {boolean} darkMode
 * @returns {string | null}
 */
export function resolvePillBoxBorderColor(colorName, darkMode) {
    if (!colorName) return null;
    const entry = normalizedBorderMap[colorName.toLowerCase()];
    if (!entry) return null;
    return darkMode ? entry.dark : entry.light;
}

export default colorMap;
